import TagManager from "react-gtm-module";
import { service } from "../state_management/actionTypes";
import { ga_id } from "../settings";
import { currency, relu } from "./misc";
import { service_types } from "../state_management/reducers/cart";

declare global {
  interface Window {
    google_tag_manager: {
      [key: string]: { dataLayer?: { reset: () => void } };
    };
  }
}

type payment_type = "Credit Card" | "Apple/Google Pay";

type gen_ga_type = {
  currency: currency;
  value: number;
};

type ga_item = {
  item_id: number;
  item_name: string;
  coupon?: string;
  price: number;
  quantity?: number;
  extra_service: boolean;
};

type ga_ecommerce = gen_ga_type & {
  items: ga_item[];
};

export type public_items = {
  id: number;
  quantity: number;
  type: service_types;
  item: {
    name: string;
    price_with_discount: number;
  };
}[];

export type ga_params_type = Omit<ga_ecommerce, "items"> & {
  items: public_items;
};

type CherryPick<T extends { items: any }> = Omit<T, "items"> &
  Pick<ga_ecommerce, "items">;

type event =
  | "add_to_cart"
  | "view_cart"
  | "remove_from_cart"
  | "begin_checkout"
  | "add_shipping_info"
  | "add_payment_info"
  | "purchase"
  | "view_item_list";

const isGTMAllowed = () => Boolean(window.tarteaucitron?.state?.ga_gtm);

const reset = () => {
  try {
    if (!isGTMAllowed()) return;

    if (window.google_tag_manager && window.google_tag_manager[ga_id]) {
      window.google_tag_manager[ga_id].dataLayer?.reset();
    }

    TagManager.dataLayer({
      dataLayer: undefined
    });
  } catch (err) {
    console.log(err);
  }
};

const callAnalytics = (dataParams: { event: event; params: object }) => {
  if (!isGTMAllowed()) return;

  const params = { event: dataParams.event, ...dataParams.params };
  if (ga_id) {
    // Clear the previous ecommerce object
    reset();

    const dataLayer = {
      event: params.event,
      ecommerce: dataParams.params
    };
    TagManager.dataLayer({
      dataLayer: dataLayer
    });
  }
};

const convertItem = (
  items: public_items
): Pick<ga_ecommerce, "items">["items"] => {
  return items.map((item) => ({
    extra_service: item.type === "extra",
    item_id: item.id,
    item_name: item.item.name,
    price: item.item.price_with_discount,
    quantity: item.quantity
  }));
};

// done
export const add_cart_ga = (params: ga_params_type) => {
  const ga_item: ga_ecommerce = {
    currency: params.currency,
    value: params.value,
    items: convertItem(params.items)
  };

  callAnalytics({
    event: "add_to_cart",
    params: ga_item
  });
};

// done
export const view_cart_ga = (params: ga_params_type) => {
  const ga_item: ga_ecommerce = {
    currency: params.currency,
    value: params.value,
    items: convertItem(params.items)
  };

  callAnalytics({
    event: "view_cart",
    params: ga_item
  });
};

// done
export const remove_from_cart_ga = (params: ga_params_type) => {
  const ga_item: ga_ecommerce = {
    currency: params.currency,
    items: convertItem(params.items),
    value: params.value
  };

  callAnalytics({
    event: "remove_from_cart",
    params: ga_item
  });
};

// done
export const begin_checkout_ga = (params: ga_params_type) => {
  const ga_item: ga_ecommerce = {
    currency: params.currency,
    value: params.value,
    items: convertItem(params.items)
  };

  callAnalytics({
    event: "begin_checkout",
    params: ga_item
  });
};

// done
export type add_shipping_info_params = ga_params_type & {
  coupon: string | null | undefined;
};
export const add_shipping_info_ga = (params: add_shipping_info_params) => {
  const ga_item: CherryPick<add_shipping_info_params> = {
    coupon: params.coupon,
    currency: params.currency,
    value: params.value,
    items: convertItem(params.items)
  };

  callAnalytics({
    event: "add_shipping_info",
    params: ga_item
  });
};

// done
export type add_payment_info_params = ga_params_type & {
  coupon: string | null | undefined;
  payment_type?: payment_type | undefined;
};
export const add_payment_info_ga = (params: add_payment_info_params) => {
  const ga_item: CherryPick<add_payment_info_params> = {
    coupon: params.coupon,
    currency: params.currency,
    value: params.value,
    items: convertItem(params.items)
  };

  callAnalytics({
    event: "add_payment_info",
    params: ga_item
  });
};

// done
type ga_view_list_type = {
  item_list_id?: number;
  extra: boolean;
  item_list_name: string;
  items: service[];
};
export const view_list_item_ga = (params: ga_view_list_type) => {
  const ga_item: Omit<ga_view_list_type, "items" | "extra"> & {
    items: ga_item[];
  } = {
    item_list_name: params.item_list_name,
    item_list_id: params.item_list_id,
    items: params.items.map((item) => ({
      extra_service: params.extra,
      item_id: item.id,
      item_name: item.name,
      price: item.price,
      discount: relu(item.price - item.price_with_discount)
    }))
  };

  callAnalytics({
    event: "view_item_list",
    params: ga_item
  });
};

//
export type purchase_params = ga_params_type & {
  transaction_id?: number;
  coupon: string | null | undefined;
  payment_type?: payment_type | undefined;
};
export const purchase_ga = (params: purchase_params) => {
  const ga_item: CherryPick<purchase_params> = {
    transaction_id: params.transaction_id,
    coupon: params.coupon,
    currency: params.currency,
    value: params.value,
    items: convertItem(params.items)
  };

  callAnalytics({
    event: "purchase",
    params: ga_item
  });
};
